import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import { useNotifications } from 'src/notifications';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import type { IShowing } from 'src/api/landlord-showings-api';


type ICancelTourResponse = {
    tour: Omit<IShowing, 'start_date'> & { start_date: string };
    has_sync_error: boolean;
};

export type ICancelTourPayload = {
    tourId: number;
}

export const useCancelShowing = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        ICancelTourResponse,
        IStandardErrorResponse,
        ICancelTourPayload
    >(
        async (payload) => {
            const res = await apiClient(`api/landlord/showings/${payload.tourId}/cancel`, {
                method: 'post',
            }).json<ICancelTourResponse>();
            return res;
        }, {
            onSuccess: (res) => {
                if (res.has_sync_error) {
                    addNotification(
                        'Tour canceled, but its calendar event could not be removed',
                        'warning',
                    );
                } else {
                    addNotification('Tour canceled', 'success');
                }
                // The showings query key now includes the date window, so a
                // prefix invalidation refreshes every loaded window (a fixed-key
                // setQueryData would no longer match any cached query).
                queryClient.invalidateQueries(queryKeys.showings);
            }, onError: (error) => {
                const errorMessage = error.msg || 'Failed to cancel tour';
                addNotification(errorMessage, 'error');
            }
        });
};
